/**
 * 9ボール ルールエンジン（WPA 準拠・純粋 reducer・自前・GPL/物理/DOM 非依存）。
 * 設計: knowledge/architecture/nineball-rules-engine.md（判定表 §4 / プロキシ §8.1）。
 *
 * dispatch(state, action, params) は副作用なしで { 新状態, 結果 } を返す。
 * 単独プレイ（ローカル2人交代）にもオンライン（権威者同期）にも同一コードで使う。
 */

import type {
  PlayerId,
  FoulReason,
  Phase,
  PlayerState,
  RuleState,
  ShotObservation,
  RuleAction,
  RuleParams,
  DoubleHitParams,
  ShotResult,
  DispatchOutcome,
} from './types.js';

const NINE = 9;
const BREAK_RAIL_MIN = 4; // WPA 既定: 合法ブレイクに必要なレール到達球数
const BREAK_POINT_MIN = 3; // 3ポイントルール: 落球＋ヘッドストリング越えの必要合計
const THREE_FOUL = 3; // 連続ファウル敗北のしきい値

/** 二度撞きプロキシの既定パラメータ（既定は無効＝ハウスルール）。 */
export const defaultDoubleHitParams = (): DoubleHitParams => ({
  enabled: false,
  gapMaxM: 0.005, // 5mm（≈0.175R）
  elevationMaxRad: Math.PI / 6, // 30°
  cutAngleMaxRad: (70 * Math.PI) / 180, // 70°
});

/** ルール挙動の既定パラメータ（既定はソロ＝オフライン単独プレイ）。 */
export const defaultRuleParams = (): RuleParams => ({
  doubleHit: defaultDoubleHitParams(),
  online: false,
  breakThreePoint: false,
});

const other = (p: PlayerId): PlayerId => (p === 0 ? 1 : 0);

const distinctCount = (xs: readonly number[]): number => new Set(xs).size;

/** プレイヤー 1 人分の状態を差し替えた新タプルを返す。 */
const withPlayer = (
  players: readonly [PlayerState, PlayerState],
  id: PlayerId,
  next: PlayerState,
): [PlayerState, PlayerState] =>
  id === 0 ? [next, players[1]] : [players[0], next];

/** 中立（イベントなし）の結果。 */
const neutralResult = (): ShotResult => ({
  foul: false,
  foulReasons: [],
  pottedIds: [],
  turnPassed: false,
  ballInHandAwarded: false,
  nineRespotted: false,
  gameOver: false,
  winner: null,
  winReason: null,
  threeFoulWarning: false,
});

export interface InitOptions {
  readonly breaker?: PlayerId;
}

/** 初期状態（ブレイク配置・ブレイカーがショット権）。 */
export const initRuleState = (opts: InitOptions = {}): RuleState => {
  const breaker = opts.breaker ?? 0;
  return {
    mode: 'nineBall',
    current: breaker,
    breaker,
    phase: 'breakPlacement',
    pushOutDeclared: false,
    players: [
      { consecutiveFouls: 0, foulWarned: false },
      { consecutiveFouls: 0, foulWarned: false },
    ],
    winner: null,
    winReason: null,
  };
};

// ---- 反則判定（設計 §4.2 ① / §4.3 / §4.4） ----

/**
 * 合法ブレイク。1番先接触は両方式で必須。
 * - 3ポイントルール（params.breakThreePoint）: 落球数 ＋ ヘッドストリング越え本数 ≥ 3。
 * - WPA 既定: 1球以上落球 or 4球以上レール到達。
 */
const isLegalBreak = (obs: ShotObservation, params: RuleParams): boolean => {
  if (obs.firstContactId !== 1) return false;
  if (params.breakThreePoint) {
    return obs.pottedIds.length + obs.headStringCrossings >= BREAK_POINT_MIN;
  }
  return (
    obs.pottedIds.length >= 1 ||
    distinctCount(obs.railedNumberedBalls) >= BREAK_RAIL_MIN
  );
};

/** 通常ショットの反則理由。 */
const normalFouls = (obs: ShotObservation, params: RuleParams): FoulReason[] => {
  const reasons: FoulReason[] = [];
  if (obs.cueScratched) reasons.push('scratch');
  if (obs.firstContactId === null) {
    reasons.push('noHit');
  } else if (obs.firstContactId !== obs.targetBallId) {
    reasons.push('wrongBall');
  } else if (obs.pottedIds.length === 0 && !obs.railAfterContact) {
    // 正接触したのに落球もレールもない＝ノークッション。
    reasons.push('noRail');
  }
  if (obs.objectBallOffTable) reasons.push('objectBallOffTable');
  if (params.doubleHit.enabled && obs.doubleHitSuspected) reasons.push('doubleHit');
  return reasons;
};

/** ブレイクの反則理由。 */
const breakFouls = (obs: ShotObservation, params: RuleParams): FoulReason[] => {
  const reasons: FoulReason[] = [];
  if (obs.cueScratched) reasons.push('scratch');
  if (obs.objectBallOffTable) reasons.push('objectBallOffTable');
  if (!isLegalBreak(obs, params)) reasons.push('illegalBreak');
  if (params.doubleHit.enabled && obs.doubleHitSuspected) reasons.push('doubleHit');
  return reasons;
};

/** プッシュアウトの反則理由（noHit/wrongBall/noRail は不問）。 */
const pushOutFouls = (obs: ShotObservation, params: RuleParams): FoulReason[] => {
  const reasons: FoulReason[] = [];
  if (obs.cueScratched) reasons.push('scratch');
  if (obs.objectBallOffTable) reasons.push('objectBallOffTable');
  if (params.doubleHit.enabled && obs.doubleHitSuspected) reasons.push('doubleHit');
  return reasons;
};

// ---- 結果の組み立て（共通） ----

/** 9番合法落球＝勝ち。 */
const resolveWin = (
  state: RuleState,
  winner: PlayerId,
  potted: readonly number[],
): DispatchOutcome => ({
  state: {
    ...state,
    phase: 'gameOver',
    pushOutDeclared: false,
    winner,
    winReason: 'pocket9',
  },
  result: {
    ...neutralResult(),
    pottedIds: potted,
    gameOver: true,
    winner,
    winReason: 'pocket9',
  },
});

/** ファウル処理（連続ファウル加算・スリーファウル敗北・相手BIH）。 */
const resolveFoul = (
  state: RuleState,
  reasons: readonly FoulReason[],
  nineRespotted: boolean,
  potted: readonly number[],
  params: RuleParams,
): DispatchOutcome => {
  // ソロ（オフライン）: ターン交代もスリーファウルもなし。BIH を付与して同一プレイヤー続行。
  if (!params.online) {
    return {
      state: {
        ...state,
        phase: 'ballInHand',
        pushOutDeclared: false,
        players: withPlayer(state.players, state.current, {
          consecutiveFouls: 0,
          foulWarned: false,
        }),
      },
      result: {
        ...neutralResult(),
        foul: true,
        foulReasons: reasons,
        pottedIds: potted,
        ballInHandAwarded: true,
        nineRespotted,
      },
    };
  }
  const offender = state.current;
  const prev = state.players[offender];
  const fouls = prev.consecutiveFouls + 1;

  // スリーファウル: 相手の勝ち。
  if (fouls >= THREE_FOUL) {
    const players = withPlayer(state.players, offender, {
      consecutiveFouls: fouls,
      foulWarned: true,
    });
    const winner = other(offender);
    return {
      state: {
        ...state,
        players,
        phase: 'gameOver',
        pushOutDeclared: false,
        winner,
        winReason: 'threeFoul',
      },
      result: {
        ...neutralResult(),
        foul: true,
        foulReasons: reasons,
        pottedIds: potted,
        nineRespotted,
        turnPassed: true,
        gameOver: true,
        winner,
        winReason: 'threeFoul',
      },
    };
  }

  const warned = fouls >= THREE_FOUL - 1; // 2 で警告
  const players = withPlayer(state.players, offender, {
    consecutiveFouls: fouls,
    foulWarned: warned,
  });
  return {
    state: {
      ...state,
      players,
      current: other(offender),
      phase: 'ballInHand',
      pushOutDeclared: false,
      winner: null,
      winReason: null,
    },
    result: {
      ...neutralResult(),
      foul: true,
      foulReasons: reasons,
      pottedIds: potted,
      turnPassed: true,
      ballInHandAwarded: true,
      nineRespotted,
      threeFoulWarning: warned && !prev.foulWarned,
    },
  };
};

/** 合法ショット（勝ち以外）。落球で継続、無落球で交代。連続ファウルをリセット。 */
const resolveLegal = (
  state: RuleState,
  potted: readonly number[],
  nextPhase: Phase,
  params: RuleParams,
): DispatchOutcome => {
  const players = withPlayer(state.players, state.current, {
    consecutiveFouls: 0,
    foulWarned: false,
  });
  // ソロ（オフライン）: ターン交代せず常に inPlay で続行。
  if (!params.online) {
    return {
      state: { ...state, players, phase: 'inPlay', pushOutDeclared: false },
      result: { ...neutralResult(), pottedIds: potted, turnPassed: false },
    };
  }
  const pottedSomething = potted.length >= 1;
  const current = pottedSomething ? state.current : other(state.current);
  return {
    state: { ...state, players, current, phase: nextPhase, pushOutDeclared: false },
    result: {
      ...neutralResult(),
      pottedIds: potted,
      turnPassed: !pottedSomething,
    },
  };
};

// ---- フェーズ別のショット解決 ----

const resolveBreak = (
  state: RuleState,
  obs: ShotObservation,
  params: RuleParams,
): DispatchOutcome => {
  const fouls = breakFouls(obs, params);
  const ninePotted = obs.pottedIds.includes(NINE);
  if (fouls.length === 0) {
    if (ninePotted) return resolveWin(state, state.current, obs.pottedIds);
    // 合法ブレイク・勝ち以外: オンラインは次の1打にプッシュアウト権、ソロは通常進行。
    const next: Phase = params.online ? 'pushOutOption' : 'inPlay';
    return resolveLegal(state, obs.pottedIds, next, params);
  }
  // 反則ブレイク: BIH（9 が落ちていれば再設置）。落ちた的玉は戻さない。
  return resolveFoul(state, fouls, ninePotted, obs.pottedIds, params);
};

const resolvePushOut = (
  state: RuleState,
  obs: ShotObservation,
  params: RuleParams,
): DispatchOutcome => {
  const fouls = pushOutFouls(obs, params);
  const ninePotted = obs.pottedIds.includes(NINE);
  if (fouls.length > 0) {
    return resolveFoul(state, fouls, ninePotted, obs.pottedIds, params);
  }
  // 合法プッシュアウト: 相手が「撞く/返す」を選択（9 落球は勝ちにせず再設置）。
  const players = withPlayer(state.players, state.current, {
    consecutiveFouls: 0,
    foulWarned: false,
  });
  return {
    state: {
      ...state,
      players,
      current: other(state.current),
      phase: 'pushOutResponse',
      pushOutDeclared: false,
    },
    result: {
      ...neutralResult(),
      pottedIds: obs.pottedIds,
      nineRespotted: ninePotted,
      turnPassed: true,
    },
  };
};

const resolveNormalShot = (
  state: RuleState,
  obs: ShotObservation,
  params: RuleParams,
): DispatchOutcome => {
  const fouls = normalFouls(obs, params);
  const ninePotted = obs.pottedIds.includes(NINE);
  if (fouls.length === 0) {
    if (ninePotted) return resolveWin(state, state.current, obs.pottedIds);
    return resolveLegal(state, obs.pottedIds, 'inPlay', params);
  }
  return resolveFoul(state, fouls, ninePotted, obs.pottedIds, params);
};

// ---- dispatch（純粋 reducer 本体） ----

/**
 * アクションを適用し { 新状態, 結果 } を返す。
 * 終端（gameOver）後の適用や、フェーズに合わないアクションは Error を投げる
 * （呼び出し側で try/catch する前提）。
 */
export const dispatch = (
  state: RuleState,
  action: RuleAction,
  params: RuleParams = defaultRuleParams(),
): DispatchOutcome => {
  if (state.phase === 'gameOver') {
    throw new Error('dispatch: ゲームは既に終了しています');
  }

  switch (action.type) {
    case 'declarePushOut': {
      if (state.phase !== 'pushOutOption') {
        throw new Error('declarePushOut: このフェーズでは宣言できません');
      }
      return {
        state: { ...state, pushOutDeclared: true },
        result: neutralResult(),
      };
    }

    case 'pushOutResponse': {
      if (state.phase !== 'pushOutResponse') {
        throw new Error('pushOutResponse: 応答できるフェーズではありません');
      }
      // current = 応答者。撞く→そのまま継続、返す→プッシュした側へ。
      const current = action.takeShot ? state.current : other(state.current);
      return {
        state: { ...state, current, phase: 'inPlay', pushOutDeclared: false },
        result: neutralResult(),
      };
    }

    case 'shot': {
      switch (state.phase) {
        case 'breakPlacement':
          return resolveBreak(state, action.obs, params);
        case 'pushOutOption':
          return state.pushOutDeclared
            ? resolvePushOut(state, action.obs, params)
            : resolveNormalShot(state, action.obs, params);
        case 'inPlay':
        case 'ballInHand':
          return resolveNormalShot(state, action.obs, params);
        default:
          throw new Error(`shot: フェーズ ${state.phase} ではショットできません`);
      }
    }

    default: {
      // 到達不能（型で網羅）。将来アクション追加時の保険。
      throw new Error('dispatch: 未知のアクションです');
    }
  }
};
